import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import { FormSelectInput } from "form";
import { actionInputName } from "components/action-modal/utils/inputName";
import { sortObjectsByProperty } from "utils/sortUtils";

const PesticideSelectInput = ({ isProposed, isViewMode, substances }) => {
  const { t } = useTranslation();

  const { values } = useFormikContext();
  const isDisabled = values?.drugAction?.bio == null;

  const activeSubstanceGroups = useActiveSubstanceGroups(substances);
  const activeSubstanceOptions = useActiveSubstanceOptions({
    substances,
    isViewMode,
    isProposed,
  });

  return (
    <FormSelectInput
      name={actionInputName("drugAction", "pesticideId", isProposed)}
      label={isViewMode ? t("ActionModal.ActiveSubstance") : undefined}
      placeholder={t("ActionModal.ActiveSubstance")}
      shouldHaveErrorBehaviorAfterSubmit
      isDisabled={isDisabled}
      isViewMode={isViewMode}
      groups={activeSubstanceGroups}
      options={activeSubstanceOptions}
    />
  );
};

export default PesticideSelectInput;

const useActiveSubstanceOptions = ({
  substances = {},
  isViewMode,
  isProposed,
}) => {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");

  const { values } = useFormikContext();

  const activeSubstanceOptions = useMemo(() => {
    if (isViewMode && isProposed) {
      return [
        {
          value: values?.proposedAction?.pesticideId,
          label: values?.proposedAction?.activeSubstance,
          group: "notRecommended",
        },
      ];
    }
    return substances
      ? Object.entries(substances)
          ?.map((entry) => {
            const [pesticideId, { group, value: substances }] = entry;
            const [firstSubstanceInstance] = substances;

            const name = getSubstanceName(firstSubstanceInstance, isEn);
            return {
              label: name || "--None--",
              value: pesticideId,
              group,
            };
          })
          .sort((a, b) => sortObjectsByProperty(a, b, "label"))
      : [];
  }, [substances, isEn, values?.proposedAction, isViewMode, isProposed]);

  return activeSubstanceOptions;
};

const useActiveSubstanceGroups = (substances = {}) => {
  const { t } = useTranslation();

  const shouldHaveRecommended = Object.values(substances).filter(
    ({ group }) => group === "recommended",
  ).length;

  const activeSubstanceGroups = shouldHaveRecommended
    ? [
        {
          value: "recommended",
          title: t("ActionModal.RecommendedActiveSubstances"),
        },
        {
          value: "notRecommended",
          title: t("ActionModal.NotRecommendedActiveSubstances"),
        },
      ]
    : [
        {
          value: "notRecommended",
          title: t("ActionModal.NoRecommendedActiveSubstanceFound"),
        },
      ];

  return activeSubstanceGroups;
};

const getSubstanceName = (substance, isEn) =>
  isEn
    ? substance?.agrochemicalRead?.name?.nameEn
    : substance?.agrochemicalRead?.name?.nameGr;
